import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TenantApiError } from "@qr-menu/api-tenant";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";
import { writeTenantToken } from "../lib/session";

const REQUIRED_FIELDS_MESSAGE =
  "Ad, soyad, e-posta, parola ve işletme adı zorunludur.";
const CONFLICT_MESSAGE =
  "Bu e-posta adresi veya işletme bağlantısı zaten kullanılıyor.";
const VALIDATION_MESSAGE = "Girilen bilgiler geçersiz. Lütfen alanları kontrol edin.";
const GENERIC_FAILURE_MESSAGE = "Kayıt oluşturulamadı. Lütfen tekrar deneyin.";

export function RegisterRoute() {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessSlug, setBusinessSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    const trimmedName = name.trim();
    const trimmedSurname = surname.trim();
    const trimmedEmail = email.trim();
    const trimmedBusinessName = businessName.trim();

    if (
      !trimmedName ||
      !trimmedSurname ||
      !trimmedEmail ||
      !password ||
      !trimmedBusinessName
    ) {
      setError(REQUIRED_FIELDS_MESSAGE);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await tenantApi.register({
        name: trimmedName,
        surname: trimmedSurname,
        email: trimmedEmail,
        phone: phone.trim(),
        password,
        business_name: trimmedBusinessName,
        business_slug: businessSlug.trim(),
      });
      writeTenantToken(res.access_token);
      setPassword("");
      navigate("/app", { replace: true });
    } catch (err) {
      let message = GENERIC_FAILURE_MESSAGE;
      if (err instanceof TenantApiError) {
        if (err.status === 409) {
          message = CONFLICT_MESSAGE;
        } else if (err.status === 400 || err.status === 422) {
          message = VALIDATION_MESSAGE;
        }
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        <CardHeader>
          <CardTitle>Hesap oluştur</CardTitle>
          <CardDescription>
            İşletmeniz için yeni bir hesap oluşturun. Oturum yalnızca
            bellekte tutulur; sayfayı yenilemek oturumu sonlandırır.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="login-form" onSubmit={handleSubmit}>
            <Field label="Ad" htmlFor="register-first-name">
              <input
                id="register-first-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </Field>
            <Field label="Soyad" htmlFor="register-last-name">
              <input
                id="register-last-name"
                type="text"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                autoComplete="family-name"
                required
              />
            </Field>
            <Field label="E-posta" htmlFor="register-email">
              <input
                id="register-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                spellCheck={false}
                required
              />
            </Field>
            <Field label="Telefon" htmlFor="register-phone">
              <input
                id="register-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                spellCheck={false}
              />
            </Field>
            <Field label="Parola" htmlFor="register-password">
              <input
                id="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                required
              />
            </Field>
            <Field label="İşletme adı" htmlFor="register-business-name">
              <input
                id="register-business-name"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                autoComplete="organization"
                required
              />
            </Field>
            <Field
              label="İşletme bağlantısı (opsiyonel)"
              htmlFor="register-business-slug"
            >
              <input
                id="register-business-slug"
                type="text"
                value={businessSlug}
                onChange={(e) => setBusinessSlug(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            {error && <div className="state error">{error}</div>}
            <div className="login-actions">
              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? "Gönderiliyor…" : "Hesap oluştur"}
              </button>
            </div>
          </form>
          <p className="hint">
            Zaten hesabınız var mı? <Link to="/login">Giriş yapın</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
